import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { auth } from '../../config/firebase';
import { onAuthStateChanged, User } from 'firebase/auth';
import TimeAgo from 'react-time-ago';
import JavascriptTimeAgo from 'javascript-time-ago';
import en from 'javascript-time-ago/locale/en';
import './CaseDetailPage.css';
import FinancasEmails from './FinancasEmails';
import FinancasOfficePicker, { SubmitToFinancasDialog } from '../FinancasOfficePicker';
import {
  FinancasOffice,
  errorMessageFrom,
  setCaseFinancasOffice,
  setCaseFinancasReference,
} from '../../api/financasOffices';
import CaseAttribution, { CaseAttributionData } from '../CaseAttribution';
import CaseTenantBadge, { CaseTenantData } from '../CaseTenantBadge';
import DocumentThumbnail from './DocumentThumbnail';
import DocumentScanRow, { DocumentScan, IDENTITY_FILE_TYPES } from './DocumentScanRow';
import { fetchDocumentScanSignedUrl } from '../../api/caseDocuments';
import PoaWarningsBanner from './PoaWarningsBanner';
import CustomerCorrectionsBanner from './CustomerCorrectionsBanner';
import ApplicantGuardians from './ApplicantGuardians';
import MinorTag from '../MinorTag';
import {
  MinorCaseFields,
  certifiedDocuments,
  isMinorCase,
  minorDocumentLabel,
  sortMinorDocumentsForDisplay,
} from './minorCase';
import NifNumberPanel from './NifNumberPanel';
import PoaCertificationPanel from './PoaCertificationPanel';
import CertifiedPoaDownloadButton from './CertifiedPoaDownloadButton';
import { PoaCertification } from '../../api/certifications';
import CustomerRequests from './CustomerRequests';
import PassportCertificationPanel from './PassportCertificationPanel';
import CertifyAllPanel from './CertifyAllPanel';
import { CertificationRun, PassportCertificationRecord } from '../../api/passportCertification';
import { NifExtraction, setCaseNifNumber } from '../../api/nifNumber';

// How often the page re-reads the case while the backend is still reading the
// NIF off a document that was just uploaded, and for how long at most.
export const NIF_READ_POLL_MS = 3000;
export const NIF_READ_POLL_LIMIT = 40;

// Uploads that document-scan-service cleans: the proof of address (re-rendered page) and passports (cut out).
const SCANNED_FILE_TYPES = new Set<string>(['proof_of_address', 'birth_certificate', ...Array.from(IDENTITY_FILE_TYPES)]);

JavascriptTimeAgo.addDefaultLocale(en);

interface Document {
  id: string;
  fileName: string;
  fileType: string;
  gcsPath: string;
  // National IDs upload as two files; the user backend tags each side.
  docSide?: 'front' | 'back';
  // Cleaned page derived by document-scan-service (proof-of-address photos only).
  scan?: DocumentScan;
  [key: string]: any;
}

const DOC_SIDE_LABEL: Record<'front' | 'back', string> = {
  front: 'Front',
  back: 'Back',
};

// Front before back within the same fileType; everything else keeps API order.
const sortDocumentsForDisplay = (docs: Document[]): Document[] => {
  const sideRank = { front: 0, back: 1 } as const;
  return [...docs].sort((a, b) => {
    if (a.fileType !== b.fileType) return 0;
    return (sideRank[a.docSide as 'front' | 'back'] ?? 2) - (sideRank[b.docSide as 'front' | 'back'] ?? 2);
  });
};

interface Case extends MinorCaseFields {
  id: string;
  /** The identity document the case was filed with: "passport" or "id_card". */
  type?: string | null;
  service: string;
  created_at: any;
  status?: string;
  lawyer_status: string;
  documents: Document[];
  final_documents: Document[];
  first_name?: string;
  middle_name?: string;
  last_name?: string;
  birthdate?: string;
  user_email?: string;
  nationality?: string;
  // The customer's address, the starting point when asking them to confirm it.
  street?: string;
  postal_code?: string;
  city?: string;
  requires_passport_video?: boolean;
  passport_validation?: {
    status?: string;
    attempts?: number;
    video_storage_path?: string;
  };
  passport_copy?: {
    fileName?: string;
  };
  passport_consent?: {
    given?: boolean;
    given_at?: any;
  };
  bar_registration?: {
    registration_number?: string;
    consultation_code?: string;
  };
  financas_office_id?: string | null;
  financas_office_name?: string | null;
  financas_reference?: string | null;
  // The customer's NIF, read off the final document or typed by the lawyer.
  nif_number?: string | null;
  nif_number_source?: 'extracted' | 'lawyer' | null;
  nif_number_updated_by?: string | null;
  nif_extraction?: NifExtraction | null;
  lawyer_submitted_at?: any;
  attribution?: CaseAttributionData;
  /** Present only on white-label cases. Absent means Coepi. */
  tenant?: CaseTenantData;
  poa_warnings?: string[];
  /** Set once an outside lawyer certified or declined the signed POA. */
  poa_certification?: PoaCertification | null;
  /** The copy certification registered with the Ordem by pt-certificate, or why the last run stopped. */
  passport_certification?: PassportCertificationRecord | null;
  /** pt-certificate's run in progress or last finished, step by step. */
  passport_certification_run?: CertificationRun | null;
  /** On a child's case, each parent's act and run, beside the child's own above. */
  passport_certification_guardian_1?: PassportCertificationRecord | null;
  passport_certification_run_guardian_1?: CertificationRun | null;
  passport_certification_guardian_2?: PassportCertificationRecord | null;
  passport_certification_run_guardian_2?: CertificationRun | null;
}

// Firestore timestamps arrive as ISO strings or {seconds} / {_seconds}.
const fileTime = (value: any): number => {
  if (!value) return 0;
  if (typeof value === 'string') return Date.parse(value) || 0;
  const seconds = value.seconds ?? value._seconds;
  return typeof seconds === 'number' ? seconds * 1000 : 0;
};

// The signed POA a certification refers to: the newest one on the case.
export const newestPoaId = (documents: Document[] | undefined): string | null => {
  const poas = (documents || []).filter(doc => doc.fileType === 'power_of_attorney');
  if (poas.length === 0) return null;
  return poas.reduce((a, b) => (fileTime(b.uploadedAt) > fileTime(a.uploadedAt) ? b : a)).id;
};

const PassportVideoPlayer: React.FC<{ caseId: string }> = ({ caseId }) => {
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadVideo = async () => {
    setLoading(true);
    setError('');
    try {
      const user = auth.currentUser;
      if (!user) return;
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/cases/${caseId}/passport-video-url`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to load video');
      const data = await response.json();
      setVideoUrl(data.url);
    } catch (err: any) {
      setError(err.message || 'Failed to load video');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVideo();
  }, [caseId]);

  if (loading) return <p style={{ color: '#6b7280', fontSize: 14 }}>Loading video...</p>;
  if (error) return <p style={{ color: '#dc2626', fontSize: 14 }}>{error}</p>;
  if (!videoUrl) return null;

  return (
    <div>
      <video
        src={videoUrl}
        controls
        style={{ width: '100%', maxWidth: 480, borderRadius: 8, backgroundColor: '#000' }}
      />
      <div style={{ marginTop: 8 }}>
        <a
          href={videoUrl}
          download="passport_recording.webm"
          className="copy-btn"
          style={{ display: 'inline-flex', textDecoration: 'none', fontSize: 14 }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
          </svg>
          Download recording
        </a>
      </div>
    </div>
  );
};

const BarRegistrationForm: React.FC<{
  caseId: string;
  existingRegistration?: string;
  existingConsultationCode?: string;
  onSaved: () => void;
}> = ({ caseId, existingRegistration, existingConsultationCode, onSaved }) => {
  const [barRegistration, setBarRegistration] = useState(existingRegistration || '');
  const [barConsultationCode, setBarConsultationCode] = useState(existingConsultationCode || '');
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState('');

  const isSaved = !!(existingRegistration && existingConsultationCode);

  const handleSave = async () => {
    if (!barRegistration.trim() || !barConsultationCode.trim()) return;
    setSaving(true);
    setSaveSuccess('');
    try {
      const user = auth.currentUser;
      if (!user) return;
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/cases/${caseId}/bar-registration`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          bar_registration: barRegistration,
          bar_consultation_code: barConsultationCode,
        }),
      });
      if (!response.ok) throw new Error('Failed to save');
      setSaveSuccess('Saved');
      onSaved();
    } catch (err) {
      console.error('Failed to save bar registration:', err);
      setSaveSuccess('Error saving');
    } finally {
      setSaving(false);
    }
  };

  const handleGeneratePdf = async () => {
    setGenerating(true);
    try {
      const user = auth.currentUser;
      if (!user) return;
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/cases/${caseId}/certificate-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to generate PDF');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${caseId}_passport_certification.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Failed to generate certificate PDF:', err);
      alert(err.message || 'Failed to generate PDF');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="case-info-card">
      <h2 style={{ margin: '0 0 16px 0' }}>Bar Registration</h2>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
            Registration Number
          </label>
          <input
            type="text"
            value={barRegistration}
            onChange={(e) => setBarRegistration(e.target.value)}
            placeholder="e.g. 58735L/49"
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: 6,
              fontSize: 14,
              boxSizing: 'border-box',
            }}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
            Consultation Code
          </label>
          <input
            type="text"
            value={barConsultationCode}
            onChange={(e) => setBarConsultationCode(e.target.value)}
            placeholder="e.g. 52700695-028017"
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: 6,
              fontSize: 14,
              boxSizing: 'border-box',
            }}
          />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 16 }}>
        <button
          className="copy-btn"
          onClick={handleSave}
          disabled={saving || !barRegistration.trim() || !barConsultationCode.trim()}
          style={{ opacity: saving ? 0.5 : 1 }}
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
        {isSaved && (
          <button
            className="copy-btn"
            onClick={handleGeneratePdf}
            disabled={generating}
            style={{ opacity: generating ? 0.5 : 1 }}
          >
            {generating ? 'Generating...' : 'Generate Certificate PDF'}
          </button>
        )}
        {saveSuccess && (
          <span style={{ fontSize: 13, color: saveSuccess === 'Saved' ? '#16a34a' : '#dc2626' }}>
            {saveSuccess}
          </span>
        )}
      </div>
    </div>
  );
};

interface CaseDetailPageProps {
  onStatusChange: () => void;
  /** features.customer_requests from /auth/me: offers "Ask the customer". */
  customerRequestsEnabled?: boolean;
  /** features.oa_certification from /auth/me: may certify the copy with the Ordem. */
  oaCertificationEnabled?: boolean;
}

// merge-certificate-into-nif: a case shows the passport video + bar-registration +
// certificate-PDF blocks when it has (or requires) a passport video — independent of
// the retired "Passport Certificate" service string, so merged NIF cases qualify.
export const hasPassportVideoStep = (caseDetails: {
  passport_validation?: { video_storage_path?: string };
  requires_passport_video?: boolean;
}): boolean =>
  Boolean(
    caseDetails.passport_validation?.video_storage_path || caseDetails.requires_passport_video
  );

const CaseDetailPage: React.FC<CaseDetailPageProps> = ({
  onStatusChange,
  customerRequestsEnabled = false,
  oaCertificationEnabled = false,
}) => {
  const { caseId } = useParams<{ caseId: string }>();
  const [caseDetails, setCaseDetails] = useState<Case | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [copySuccess, setCopySuccess] = useState('');
  const [statusError, setStatusError] = useState('');
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [editingOffice, setEditingOffice] = useState(false);
  const [savingOffice, setSavingOffice] = useState(false);
  const [officeError, setOfficeError] = useState('');
  const [editingReference, setEditingReference] = useState(false);
  const [referenceDraft, setReferenceDraft] = useState('');
  const [savingReference, setSavingReference] = useState(false);
  const [referenceError, setReferenceError] = useState('');
  // The final document whose NIF the backend is still reading, if any.
  const [pendingNifDocument, setPendingNifDocument] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
      } else {
        navigate('/');
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const fetchCaseDetails = async () => {
    if (user && caseId) {
      try {
        const token = await user.getIdToken();
        const response = await fetch(`${process.env.REACT_APP_API_URL}/cases/${caseId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setCaseDetails(data);
      } catch (error) {
        console.error('Error fetching case details:', error);
      }
    }
  };

  useEffect(() => {
    fetchCaseDetails();
  }, [caseId, user]);

  // The NIF is read after the upload response, so the case has to be re-read
  // until the extraction for that document shows up (or we give up).
  const extractionDocumentId = caseDetails?.nif_extraction?.document_id;
  useEffect(() => {
    if (!pendingNifDocument) return undefined;
    if (extractionDocumentId === pendingNifDocument) {
      setPendingNifDocument(null);
      return undefined;
    }
    let polls = 0;
    const timer = setInterval(() => {
      polls += 1;
      if (polls > NIF_READ_POLL_LIMIT) {
        setPendingNifDocument(null);
        return;
      }
      fetchCaseDetails();
    }, NIF_READ_POLL_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingNifDocument, extractionDocumentId, caseId, user]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFile(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file || !caseId || !user) return;

    try {
      const token = await user.getIdToken();
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/cases/${caseId}/upload-final-document`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      setFile(null);
      if (result?.nif_extraction_scheduled && result.document_id) {
        setPendingNifDocument(result.document_id);
      }

      fetchCaseDetails();
    } catch (error) {
      console.error('Error uploading file:', error);
    }
  };

  const handleViewScan = async (documentId: string) => {
    if (!user || !caseId) return;
    try {
      const token = await user.getIdToken();
      const data = await fetchDocumentScanSignedUrl(token, caseId, documentId);
      window.open(data.url, '_blank');
    } catch (error) {
      console.error('Error fetching cleaned page URL:', error);
    }
  };

  const handleNifSave = async (nifNumber: string) => {
    if (!user || !caseDetails) return;
    const token = await user.getIdToken();
    const result = await setCaseNifNumber(token, caseDetails.id, nifNumber);
    // The backend normalises the number; take back what it stored.
    setCaseDetails(prev => prev && {
      ...prev,
      nif_number: result.nif_number,
      nif_number_source: 'lawyer',
      nif_number_updated_by: user.email || prev.nif_number_updated_by,
      nif_extraction: prev.nif_extraction ? { ...prev.nif_extraction, review_needed: false } : prev.nif_extraction,
    });
  };

  const handleViewDocument = async (fileName: string) => {
    if (!user || !caseId) return;
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/cases/${caseId}/documents/${fileName}/signed-url`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      window.open(data.url, '_blank');
    } catch (error) {
      console.error('Error fetching signed URL:', error);
    }
  };

  const handleViewFinalDocument = async (fileName: string) => {
    if (!user || !caseId) return;
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/cases/${caseId}/final-documents/${fileName}/signed-url`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      window.open(data.url, '_blank');
    } catch (error) {
      console.error('Error fetching signed URL:', error);
    }
  };

  const handleDownloadFiles = async (caseId: string) => {
    if (!user) return;
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/cases/${caseId}/documents/zip`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Download failed');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${caseId}_documents.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading files:', error);
    }
  };

  const handleDownloadPdf = async (caseId: string) => {
    if (!user) return;
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/cases/${caseId}/documents/pdf`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Download failed');
      }
      const canStreamToFile = typeof window !== 'undefined' && 'showSaveFilePicker' in window && response.body;
      if (canStreamToFile) {
        const extendedWindow = window as typeof window & { showSaveFilePicker?: any };
        try {
          const fileHandle = await extendedWindow.showSaveFilePicker?.({
            suggestedName: `${caseId}_documents.pdf`,
            types: [
              {
                description: 'PDF document',
                accept: { 'application/pdf': ['.pdf'] },
              },
            ],
          });

          if (fileHandle) {
            const writable = await fileHandle.createWritable();
            const reader = response.body!.getReader();
            try {
              while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                if (value) {
                  await writable.write(value);
                }
              }
            } finally {
              await writable.close();
            }
            return;
          }
        } catch (streamError: any) {
          if (streamError?.name === 'AbortError') {
            console.info('PDF download cancelled by user before saving.');
            return;
          }
          console.warn('Streamed PDF download failed, falling back to blob download.', streamError);
        }
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${caseId}_documents.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading pdf:', error);
    }
  };



  const handleDeleteFinalDocument = async (docId: string) => {
    if (!user || !caseId) return;
    if (!window.confirm("Are you sure you want to delete this final document?")) return;

    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/cases/${caseId}/final-documents/${docId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      fetchCaseDetails();
    } catch (error) {
      console.error('Error deleting document:', error);
    }
  };

  const handleDeleteUserDocument = async (docId: string) => {
    if (!user || !caseId) return;
    if (!window.confirm("Are you sure you want to delete this document?")) return;

    try {
      const token = await user.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/cases/${caseId}/documents/${docId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      fetchCaseDetails();
    } catch (error) {
      console.error('Error deleting document:', error);
    }
  };

  const handleStatusChange = async (caseId: string, status: string) => {
    if (!user) return;
    setStatusError('');
    // Marking Submitted needs the Finanças office the case was filed at. Ask
    // for it first when the case has none; the dialog sends the status itself.
    if (
      status === 'Submitted'
      && caseDetails?.lawyer_status !== 'Submitted'
      && !caseDetails?.financas_office_id
    ) {
      setShowSubmitDialog(true);
      return;
    }
    const token = await user.getIdToken();
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/cases/${caseId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status }),
      });
      const data = await response.json();
      if (!response.ok) {
        setStatusError(errorMessageFrom(data, 'Failed to update the case status.'));
        fetchCaseDetails();
        return;
      }
      fetchCaseDetails();
      onStatusChange();
    } catch (error) {
      console.error('Error updating case status:', error);
      setStatusError('Failed to update the case status. Please try again.');
    }
  };

  const handleSubmittedWithOffice = (office: FinancasOffice) => {
    setShowSubmitDialog(false);
    setCaseDetails(prev => prev && {
      ...prev,
      lawyer_status: 'Submitted',
      financas_office_id: office.id,
      financas_office_name: office.name,
    });
    fetchCaseDetails();
    onStatusChange();
  };

  const handleOfficeChange = async (office: FinancasOffice) => {
    if (!user || !caseDetails) return;
    setSavingOffice(true);
    setOfficeError('');
    try {
      const token = await user.getIdToken();
      const result = await setCaseFinancasOffice(token, caseDetails.id, office.id);
      setCaseDetails(prev => prev && {
        ...prev,
        financas_office_id: result.financas_office_id ?? office.id,
        financas_office_name: result.financas_office_name ?? office.name,
      });
      setEditingOffice(false);
    } catch (error) {
      setOfficeError(error instanceof Error ? error.message : 'Failed to save the Finanças office.');
    } finally {
      setSavingOffice(false);
    }
  };

  const startEditingReference = () => {
    setReferenceDraft(caseDetails?.financas_reference ?? '');
    setReferenceError('');
    setEditingReference(true);
  };

  const handleReferenceSave = async () => {
    if (!user || !caseDetails) return;
    setSavingReference(true);
    setReferenceError('');
    try {
      const token = await user.getIdToken();
      const result = await setCaseFinancasReference(token, caseDetails.id, referenceDraft);
      // The backend normalises the reference, so take back what it stored
      // rather than what was typed.
      setCaseDetails(prev => prev && { ...prev, financas_reference: result.financas_reference });
      setEditingReference(false);
    } catch (error) {
      setReferenceError(
        error instanceof Error ? error.message : 'Failed to save the Finanças reference.'
      );
    } finally {
      setSavingReference(false);
    }
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'Complete':
      case 'COMPLETED':
        return 'status-complete';
      case 'Submitted':
        return 'status-submitted';
      case 'Closed':
        return 'status-closed';
      case 'PASSPORT_VIDEO_VALIDATION':
      case 'UPLOAD_PASSPORT_COPY':
        return 'status-open';
      default:
        return 'status-open';
    }
  };

  const getWorkflowStatusLabel = (status?: string) => {
    switch (status) {
      case 'PASSPORT_VIDEO_VALIDATION':
        return 'Passport Video Validation';
      case 'UPLOAD_PASSPORT_COPY':
        return 'Upload Passport Copy';
      case 'COMPLETED':
        return 'Completed';
      case 'LAWYER_REVIEW':
        return 'Lawyer Review';
      case 'user_completed':
        return 'User Completed';
      default:
        return status || 'Unknown';
    }
  };

  if (!caseDetails) {
    return <div>Loading...</div>;
  }

  const isCertificateCase = caseDetails.service === 'Passport Certificate';
  const hasFinalDocument = (caseDetails.final_documents?.length ?? 0) > 0;
  const isComplete = caseDetails.lawyer_status === 'Complete';
  // Merged flow (merge-certificate-into-nif): the passport video real-check and the
  // internal certification PDF now also live on NIF cases. Key these blocks off the
  // presence/need of a passport video rather than the (retired) certificate service.
  const hasPassportVideo = hasPassportVideoStep(caseDetails);
  const isMinor = isMinorCase(caseDetails);

  const textToCopy = `Boa tarde,

Eu, Thaís R. Garrido, Advogada, na qualidade de Mandatária de ${caseDetails.first_name || ''} ${caseDetails.last_name || ''}, ${caseDetails.nationality || 'Brasileira'}, venho remeter, em anexo, a documentação necessária para a atribuição de número de NIF à cidadã estrangeira, tendo em vista que declaro aceitar ser a representante legal da mesma para este efeito.

Antecipadamente grata e com os meus melhores cumprimentos,

A Advogada, Thais R. Garrido (58735L)`;

  const handleCopy = () => {
    navigator.clipboard.writeText(textToCopy).then(() => {
        setCopySuccess('Copied!');
        setTimeout(() => setCopySuccess(''), 2000);
    }, () => {
        setCopySuccess('Failed to copy!');
        setTimeout(() => setCopySuccess(''), 2000);
    });
  };

  return (
    <div className="case-detail-page">
      <PoaWarningsBanner
        warnings={caseDetails.poa_warnings}
        lawyerStatus={caseDetails.lawyer_status}
      />
      <CustomerCorrectionsBanner
        documents={caseDetails.documents}
        lawyerStatus={caseDetails.lawyer_status}
      />

      <div className="case-info-card">
        <div className="info-item">
          <span className="info-item-label">Case ID</span>
          <span className="info-item-value">{caseDetails.id}</span>
          {(caseDetails.first_name || caseDetails.last_name) && (
            <span className="info-item-client-name">
              {caseDetails.first_name} {caseDetails.last_name}
            </span>
          )}
        </div>
        {caseDetails.user_email && (
          <div className="info-item">
            <span className="info-item-label">Client Email</span>
            <span className="info-item-value">{caseDetails.user_email}</span>
          </div>
        )}
        <div className="info-item">
          <span className="info-item-label">Service</span>
          <span className="info-item-value">{caseDetails.service}</span>
        </div>
        <div className="info-item">
          <span className="info-item-label">Applicant</span>
          <MinorTag applicantType={caseDetails.applicant_type} variant="stacked" showAdult />
        </div>
        <div className="info-item">
          <span className="info-item-label">Came from</span>
          <CaseAttribution attribution={caseDetails.attribution} variant="stacked" />
        </div>
        {/* Only rendered for a white-label case. The agent is about to write
            to this person; they need to know the customer has never heard of
            Coepi before they sign off. */}
        {caseDetails.tenant && (
          <div className="info-item">
            <span className="info-item-label">Sold as</span>
            <CaseTenantBadge tenant={caseDetails.tenant} variant="stacked" />
          </div>
        )}
        <div className="info-item">
          <span className="info-item-label">Case opened</span>
          <span className="info-item-value">
            {caseDetails.created_at && (
              <TimeAgo date={new Date(caseDetails.created_at._seconds ? caseDetails.created_at._seconds * 1000 : caseDetails.created_at)} />
            )}
          </span>
        </div>
        <div className="info-item">
          <span className="info-item-label">Status</span>
          <select className="status-select" value={caseDetails.lawyer_status || 'Open'} onChange={(e) => handleStatusChange(caseDetails.id, e.target.value)}>
            {/* An Open NIF case can move in by hand, and out again. No customer email either way. */}
            {caseDetails.service === 'NIF Application' && ['Certificate', 'Open', '', undefined, null].includes(caseDetails.lawyer_status) && (
              <option value="Certificate">Certificate</option>
            )}
            <option value="Open">Open</option>
            <option value="Waiting on customer">Waiting on customer</option>
            <option value="Submitted">Submitted</option>
            <option value="Complete" disabled={!hasFinalDocument && !isComplete}>
              Complete{!hasFinalDocument && !isComplete ? ' (upload final document first)' : ''}
            </option>
            <option value="Closed">Closed</option>
          </select>
          {!hasFinalDocument && !isComplete && (
            <span className="status-hint">
              Upload the final NIF document below to mark this case as Complete.
            </span>
          )}
        </div>
        <div className="info-item">
          <span className="info-item-label">Finanças office</span>
          {editingOffice ? (
            <>
              <FinancasOfficePicker
                id="case-financas-office-select"
                value={caseDetails.financas_office_id ?? null}
                valueName={caseDetails.financas_office_name}
                onChange={handleOfficeChange}
                disabled={savingOffice}
              />
              <button
                type="button"
                className="financas-btn financas-btn--link case-office-action"
                onClick={() => { setEditingOffice(false); setOfficeError(''); }}
                disabled={savingOffice}
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <span className="info-item-value" data-testid="case-financas-office">
                {caseDetails.financas_office_name
                  ? `Filed at: ${caseDetails.financas_office_name}`
                  : 'Not recorded'}
              </span>
              <button
                type="button"
                className="financas-btn financas-btn--link case-office-action"
                onClick={() => setEditingOffice(true)}
              >
                {caseDetails.financas_office_id ? 'Change office' : 'Set office'}
              </button>
            </>
          )}
          {officeError && (
            <span className="financas-office-error" role="alert">{officeError}</span>
          )}
        </div>
        <div className="info-item">
          <span className="info-item-label">Finanças reference</span>
          {editingReference ? (
            <>
              <input
                type="text"
                id="case-financas-reference"
                className="financas-reference-input"
                aria-label="Finanças reference"
                maxLength={120}
                value={referenceDraft}
                onChange={event => setReferenceDraft(event.target.value)}
                onKeyDown={event => {
                  if (event.key === 'Enter') handleReferenceSave();
                  if (event.key === 'Escape') setEditingReference(false);
                }}
                disabled={savingReference}
                autoFocus
              />
              <button
                type="button"
                className="financas-btn financas-btn--link case-office-action"
                onClick={handleReferenceSave}
                disabled={savingReference}
              >
                {savingReference ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                className="financas-btn financas-btn--link case-office-action"
                onClick={() => { setEditingReference(false); setReferenceError(''); }}
                disabled={savingReference}
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <span className="info-item-value" data-testid="case-financas-reference">
                {caseDetails.financas_reference || 'Not recorded'}
              </span>
              <button
                type="button"
                className="financas-btn financas-btn--link case-office-action"
                onClick={startEditingReference}
              >
                {caseDetails.financas_reference ? 'Change reference' : 'Set reference'}
              </button>
            </>
          )}
          {referenceError && (
            <span className="financas-office-error" role="alert">{referenceError}</span>
          )}
        </div>
      </div>

      {statusError && (
        <div className="status-error-banner" role="alert">
          {statusError}
        </div>
      )}

      {showSubmitDialog && (
        <SubmitToFinancasDialog
          caseId={caseDetails.id}
          clientName={[caseDetails.first_name, caseDetails.last_name].filter(Boolean).join(' ') || undefined}
          onCancel={() => setShowSubmitDialog(false)}
          onSubmitted={handleSubmittedWithOffice}
        />
      )}

      {isMinor && (
        <ApplicantGuardians
          caseId={caseDetails.id}
          caseDetails={caseDetails}
          documents={caseDetails.documents ?? []}
          user={user}
          onOpenDocument={handleViewDocument}
          onOverrideSaved={override =>
            setCaseDetails(prev => prev && { ...prev, guardian_match_override: override })
          }
        />
      )}

      {!isCertificateCase && (
        <div className="copy-text-section">
          <div className="section-header">
            <h2>NIF Application Text</h2>
            <button className="copy-btn" onClick={handleCopy}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ marginRight: '8px' }}>
                <path d="M4 1.5H3a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1h1a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1h1v-1z"/>
                <path d="M9.5 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5h3z"/>
              </svg>
              {copySuccess || 'Copy'}
            </button>
          </div>
          <pre className="copy-text-content">
            {textToCopy}
          </pre>
        </div>
      )}

      {hasPassportVideo && (
        <div className="case-info-card">
          <h2 style={{ margin: '0 0 16px 0' }}>Passport Validation</h2>
          <div className="info-item">
            <span className="info-item-label">Validation Status</span>
            <span className="info-item-value">
              {caseDetails.passport_validation?.status || 'pending'}
            </span>
          </div>
          <div className="info-item">
            <span className="info-item-label">Identity Consent</span>
            <span className="info-item-value" style={{ color: caseDetails.passport_consent?.given ? '#16a34a' : '#dc2626' }}>
              {caseDetails.passport_consent?.given ? '✓ Confirmed' : 'Not confirmed'}
            </span>
          </div>
          {caseDetails.passport_copy?.fileName && (
            <div className="info-item">
              <span className="info-item-label">Passport Copy</span>
              <span className="info-item-value">
                {caseDetails.passport_copy.fileName}
              </span>
            </div>
          )}

          {caseDetails.passport_validation?.video_storage_path && (
            <div style={{ marginTop: 16 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Passport Recording</h3>
              <PassportVideoPlayer caseId={caseDetails.id} />
            </div>
          )}
        </div>
      )}

      {isMinor && (
        <CertifyAllPanel
          caseId={caseDetails.id}
          documents={certifiedDocuments(caseDetails)}
          records={{
            applicant: caseDetails.passport_certification,
            guardian_1: caseDetails.passport_certification_guardian_1,
            guardian_2: caseDetails.passport_certification_guardian_2,
          }}
          canCertify={oaCertificationEnabled}
          user={user}
          onRefresh={fetchCaseDetails}
        />
      )}
      {isMinor
        ? // A child's case has no passport video; its acts certify the child's and each parent's document.
          certifiedDocuments(caseDetails).map(({ person, title, who }) => (
            <PassportCertificationPanel
              key={person}
              caseId={caseDetails.id}
              person={person}
              title={title}
              who={who}
              record={person === 'applicant' ? caseDetails.passport_certification : caseDetails[`passport_certification_${person}`]}
              run={person === 'applicant' ? caseDetails.passport_certification_run : caseDetails[`passport_certification_run_${person}`]}
              canCertify={oaCertificationEnabled}
              user={user}
              onRefresh={fetchCaseDetails}
            />
          ))
        : (hasPassportVideo || caseDetails.passport_certification) && (
            <PassportCertificationPanel
              caseId={caseDetails.id}
              record={caseDetails.passport_certification}
              run={caseDetails.passport_certification_run}
              canCertify={oaCertificationEnabled && hasPassportVideo}
              user={user}
              onRefresh={fetchCaseDetails}
            />
          )}

      {hasPassportVideo && (
        <BarRegistrationForm
          // Remounted when a certification fills the numbers in, so the fields show them.
          key={`${caseDetails.bar_registration?.registration_number || ''}:${caseDetails.bar_registration?.consultation_code || ''}`}
          caseId={caseDetails.id}
          existingRegistration={caseDetails.bar_registration?.registration_number}
          existingConsultationCode={caseDetails.bar_registration?.consultation_code}
          onSaved={() => fetchCaseDetails()}
        />
      )}

      <FinancasEmails
        caseId={caseDetails.id}
        user={user}
        reference={caseDetails.financas_reference}
      />

      <CustomerRequests
        caseId={caseDetails.id}
        user={user}
        canAsk={customerRequestsEnabled}
        lawyerStatus={caseDetails.lawyer_status}
        applicantType={caseDetails.applicant_type}
        documentType={caseDetails.type}
        caseValues={{
          first_name: caseDetails.first_name,
          middle_name: caseDetails.middle_name,
          last_name: caseDetails.last_name,
          street: caseDetails.street,
          postal_code: caseDetails.postal_code,
          city: caseDetails.city,
        }}
        onCaseChanged={() => {
          fetchCaseDetails();
          onStatusChange();
        }}
        onViewDocument={handleViewDocument}
      />

      {caseDetails.service !== 'Passport Certificate' && newestPoaId(caseDetails.documents) && (
        <PoaCertificationPanel
          caseId={caseDetails.id}
          poaFileId={newestPoaId(caseDetails.documents) as string}
          certification={caseDetails.poa_certification}
          user={user}
          onChanged={fetchCaseDetails}
        />
      )}

      <div className="documents-section">
        <div className="section-header">
          <h2>Client Submitted Documents</h2>
          <div>
            <button className="download-all-btn" onClick={() => handleDownloadPdf(caseDetails.id)}>Download PDF</button>
            {caseDetails.service !== 'Passport Certificate' && newestPoaId(caseDetails.documents) && (
              <>
                {' '}
                <CertifiedPoaDownloadButton
                  caseId={caseDetails.id}
                  poaFileId={newestPoaId(caseDetails.documents) as string}
                  certification={caseDetails.poa_certification}
                  user={user}
                />
              </>
            )}
          </div>
        </div>
        <div className="document-list">
          {(isMinor
            ? sortMinorDocumentsForDisplay(caseDetails.documents ?? [])
            : sortDocumentsForDisplay(caseDetails.documents ?? [])
          ).map(doc => (
            <React.Fragment key={doc.id}>
              <div className="document-item">
                <DocumentThumbnail
                  caseId={caseDetails.id}
                  documentId={doc.id}
                  fileName={doc.fileName}
                  user={user}
                  onOpen={() => handleViewDocument(doc.fileName)}
                />
                <div className="document-info">
                  <div className="document-name">{doc.fileName}</div>
                  <div className="document-type">
                    {(isMinor && minorDocumentLabel(doc, caseDetails.guardians)) || doc.fileType}
                    {doc.docSide && (
                      <span className={`doc-side-badge doc-side-${doc.docSide}`}>
                        {DOC_SIDE_LABEL[doc.docSide]}
                      </span>
                    )}
                  </div>
                </div>
                <button className="view-btn" onClick={() => handleViewDocument(doc.fileName)}>View</button>
              </div>
              {SCANNED_FILE_TYPES.has(doc.fileType) && doc.scan && (
                <DocumentScanRow
                  caseId={caseDetails.id}
                  documentId={doc.id}
                  fileName={doc.fileName}
                  fileType={doc.fileType}
                  scan={doc.scan}
                  user={user}
                  onOpen={() => handleViewScan(doc.id)}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="documents-section">
        <div className="section-header">
          <h2>Documents for the client</h2>
        </div>
        {!hasFinalDocument && (
          <div className="final-doc-warning" role="alert">
            <strong>No final document uploaded yet.</strong> Upload the client's
            NIF document here — the case can't be marked Complete, and the
            client won't be notified, until you do.
          </div>
        )}
        {caseDetails.service !== 'Passport Certificate' &&
          (hasFinalDocument || pendingNifDocument || caseDetails.nif_number || caseDetails.nif_extraction) && (
          <NifNumberPanel
            nifNumber={caseDetails.nif_number}
            source={caseDetails.nif_number_source}
            updatedBy={caseDetails.nif_number_updated_by}
            extraction={caseDetails.nif_extraction}
            reading={Boolean(pendingNifDocument)}
            onSave={handleNifSave}
          />
        )}
        <div className="upload-area">
          <div className="file-input-wrapper">
            <label htmlFor="file-upload" className="choose-file-btn">
              Choose File
            </label>
            <input id="file-upload" type="file" onChange={handleFileChange} style={{ display: 'none' }} />
            <span className="file-name">{file ? file.name : 'No file chosen'}</span>
          </div>
          <button className="upload-btn" onClick={handleUpload} disabled={!file}>
            Upload Final Document
          </button>
        </div>
        <div className="document-list">
          {caseDetails.final_documents?.map(doc => (
            <div key={doc.id} className="document-item">
              <div className="document-icon">✅</div>
              <div className="document-info">
                <div className="document-name">{doc.fileName}</div>
                <div className="document-type">final_nif_document</div>
              </div>
              <button className="view-btn" onClick={() => handleViewFinalDocument(doc.fileName)}>View</button>
              <button className="delete-btn" onClick={() => handleDeleteFinalDocument(doc.id)}>Delete</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CaseDetailPage;
